import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Laptop, LogOut, MonitorPlay, RefreshCw } from 'lucide-react';
import { appModeBlockedLabel } from '@volley/crucible-ipc-types';
import { AppModeSwitchButtonChrome } from '../components/AppModeSwitch';
import { FoundryShellHeader } from '../components/FoundryShellHeader';
import { ProtoHubSimulatorSurface } from '../components/ProtoHubSimulatorSurface';

/** Keep the hosted Cloud BrowserView aligned with the Electron-owned header. */
function useCloudContentInsetReporter(headerRef: React.RefObject<HTMLElement | null>): void {
  useLayoutEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    let frame = 0;
    const reportInset = (): void => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const bottom = Math.round(header.getBoundingClientRect().bottom);
        if (bottom > 0) {
          void window.crucible.invoke('app-mode:set-cloud-content-inset', { top: bottom });
        }
      });
    };

    reportInset();

    const resizeObserver = new ResizeObserver(reportInset);
    resizeObserver.observe(header);
    for (let node: HTMLElement | null = header.parentElement; node; node = node.parentElement) {
      resizeObserver.observe(node);
    }

    const shellRoot = header.parentElement?.parentElement?.parentElement;
    let mutationObserver: MutationObserver | null = null;
    if (shellRoot instanceof HTMLElement) {
      mutationObserver = new MutationObserver(reportInset);
      mutationObserver.observe(shellRoot, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
    }

    window.addEventListener('resize', reportInset);
    return () => {
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener('resize', reportInset);
    };
  }, [headerRef]);
}

export function CloudDesktopShell(): React.JSX.Element {
  const headerRef = useRef<HTMLElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [switching, setSwitching] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [protoHubOpen, setProtoHubOpen] = useState(false);
  // crucible#2326: the game to focus on the Hub. Set per-open and cleared on
  // close, so it never goes stale — the post-deploy push carries a fresh
  // gameId; the manual "Display on Hub" button opens game-agnostic (undefined).
  const [protoHubGameId, setProtoHubGameId] = useState<string | undefined>(undefined);

  useCloudContentInsetReporter(headerRef);

  useEffect(() => {
    const unsubscribe = window.crucible.on('app-mode:cloud-session-clearing', () => {
      // External sign-out paths must reveal Cloud login, not leave the TV shell mounted.
      setProtoHubOpen(false);
      setProtoHubGameId(undefined);
    });
    return () => unsubscribe();
  }, []);

  // crucible#2326: the cloud SPA can't open the preload-less native simulator
  // itself; after a successful deploy it navigates to a sentinel that Electron
  // main classifies + validates, then main pushes here. The chrome (which DOES
  // have the preload) mounts the ProtoHubSimulatorSurface focused on the
  // deployed game — the same path the manual "Display on Hub" button uses, so
  // the surface sizes itself (protohub:set-bounds) and provides close/reload.
  useEffect(() => {
    const unsubscribe = window.crucible.on('cloud:protohub-preview', (payload) => {
      setProtoHubGameId(payload.gameId);
      setProtoHubOpen(true);
    });
    return () => unsubscribe();
  }, []);

  const switchToLocal = async (): Promise<void> => {
    setError(null);
    setSwitching(true);
    try {
      const result = await window.crucible.invoke('app-mode:set-mode', { mode: 'local' });
      if (!result.ok) {
        setError(appModeBlockedLabel(result));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSwitching(false);
    }
  };

  const signOut = async (): Promise<void> => {
    setError(null);
    setSigningOut(true);
    // Sign-out must reveal the hosted login surface, not keep the simulator shell mounted.
    setProtoHubOpen(false);
    try {
      await window.crucible.invoke('app-mode:clear-cloud-session');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-crucible-bg text-crucible-fg">
      <FoundryShellHeader
        ref={headerRef}
        chrome="electron"
        title="Cloud Foundry"
        showBrandMark
        testId="cloud-desktop-shell-header"
        actions={
          <>
            {error && (
              <p role="status" className="max-w-[280px] truncate text-[12px] text-red-200/90">
                {error}
              </p>
            )}
            <AppModeSwitchButtonChrome
              label="Display on Hub"
              icon={<MonitorPlay size={14} />}
              onClick={() => {
                // Manual open is game-agnostic (Hub home) — never reuse a stale
                // post-deploy gameId (crucible#2326).
                setProtoHubGameId(undefined);
                setProtoHubOpen(true);
              }}
              disabled={switching || signingOut || protoHubOpen}
            />
            <AppModeSwitchButtonChrome
              label={switching ? 'Opening Local...' : 'Switch to Local Foundry'}
              icon={
                switching ? <RefreshCw size={14} className="animate-spin" /> : <Laptop size={14} />
              }
              onClick={() => void switchToLocal()}
              disabled={switching || signingOut}
            />
            <button
              type="button"
              onClick={() => void signOut()}
              disabled={switching || signingOut || protoHubOpen}
              className="inline-flex h-9 items-center gap-2 whitespace-nowrap rounded-lg border border-white/[0.11] bg-white/[0.04] px-3 text-[13px] font-medium text-white/60 transition-colors hover:border-white/[0.2] hover:bg-white/[0.07] hover:text-white/85 disabled:cursor-not-allowed disabled:opacity-55"
            >
              {signingOut ? <RefreshCw size={14} className="animate-spin" /> : <LogOut size={14} />}
              <span>{signingOut ? 'Signing out...' : 'Sign out'}</span>
            </button>
          </>
        }
      />
      {protoHubOpen ? (
        <div className="min-h-0 flex-1">
          <ProtoHubSimulatorSurface
            target="cloud"
            source={protoHubGameId ? 'post-deploy' : 'manual'}
            gameId={protoHubGameId}
            onClose={() => {
              setProtoHubOpen(false);
              setProtoHubGameId(undefined);
            }}
          />
        </div>
      ) : (
        <div aria-hidden="true" className="min-h-0 flex-1" />
      )}
    </div>
  );
}
